/**
 * Marking a note forensically relevant, and removing the marker — DECISIONS.md #53 step 2.
 *
 * Mirrors `api/src/Continuum.Api/Notes/ForensicMarkerEndpoints.cs`. A marker carries no category and
 * no reason, so there is no body to send when marking; a withdrawal names the version it supersedes,
 * the shape every correction here has.
 */

import { unauthenticated } from '../auth/sessionExpiry'

/** Mirrors `WrittenForensicMarker`. */
export interface WrittenForensicMarker {
  readonly markerId: string
  readonly versionId: string
  readonly version: number
  readonly status: string
}

/** The server refused, by code. The screen says one sentence for all of them and refetches. */
export class ForensicMarkerRefusedError extends Error {
  readonly code: string

  constructor(code: string) {
    super(`Forensic marker refused: ${code}`)
    this.name = 'ForensicMarkerRefusedError'
    this.code = code
  }
}

/** No usable answer: the write may have happened. */
export class ForensicMarkerUnavailableError extends Error {
  constructor() {
    super('Forensic marker unavailable')
    this.name = 'ForensicMarkerUnavailableError'
  }
}

async function post(url: string, body: unknown, signal: AbortSignal): Promise<WrittenForensicMarker> {
  let response: Response

  try {
    response = await fetch(url, {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
  } catch (cause) {
    if (signal.aborted) {
      throw cause
    }
    throw new ForensicMarkerUnavailableError()
  }

  unauthenticated(response)

  if (!response.ok) {
    let code = 'UNKNOWN'
    try {
      const parsed = (await response.json()) as { error?: { code?: unknown } }
      if (typeof parsed.error?.code === 'string') {
        code = parsed.error.code
      }
    } catch {
      // The server answered, so nothing was written; the code is simply unknown.
    }
    throw new ForensicMarkerRefusedError(code)
  }

  try {
    return (await response.json()) as WrittenForensicMarker
  } catch {
    throw new ForensicMarkerUnavailableError()
  }
}

/** Marks one of the patient's notes. */
export function markNote(patientId: string, noteId: string, signal: AbortSignal) {
  return post(
    `/api/patients/${encodeURIComponent(patientId)}/notes/${encodeURIComponent(noteId)}/forensic-markers`,
    {},
    signal,
  )
}

/** Withdraws a marker, naming the version the screen was showing. */
export function withdrawMarker(
  patientId: string,
  markerId: string,
  supersedes: string,
  signal: AbortSignal,
) {
  return post(
    `/api/patients/${encodeURIComponent(patientId)}/forensic-markers/${encodeURIComponent(markerId)}/versions`,
    { supersedes },
    signal,
  )
}
